import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { withApi } from '@tencent/react-aio';
import { Table, Dialog, FormGroup, Input, DatePicker, Column, PageBar, OverlayTrigger, PopOver } from '@tencent/spaui';
import TeamUserSelectWithoutSeat from '@/business-components/sale/TeamUserSelectWithoutSeat';
import WeChatLabelSelect from '@/business-components/sale/WeChatLabelSelect';
import { OPTIONAL_START_RELATIVE_DAYS, WECHAT_CUSTOMER_STATUS } from '@/constants/Constants';
import { fuzzyGetMessage } from '@/utils/MessageUtils';
import { withAccount } from '@/business-components/operator/account-context';
import _ from 'lodash';
import './style.scoped.less';
import '@/assets/less/modules/with-light-theme.scoped.less';
import message from '@/utils/message';
import { User } from '@/model/user';

interface WeChatAssociateProps {
  webApi: {
    searchRelation: () => any;
    onlineSalesBind: () => any;
  };
  titleText: string;
  onSubmit: (value: any) => void;
  relation: {
    relationId: number;
    contactId: number;
    leadId: number;
  };
  callback: () => void;
  account: User;
  mode: 'default' | 'input';
  inputValue: string;
}

interface PageConfig {
  pageSize: number;
  totalCount: number;
}

const baseStyle = { width: 150 };

const WeChatAssociateModal: React.FC<WeChatAssociateProps> = (props) => {
  const { webApi, titleText = '', relation, mode = 'default' } = props;
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [page, setPage] = useState<PageConfig>({
    pageSize: 20,
    totalCount: 0,
  });
  const [id, setId] = useState<number | null>(relation?.relationId);
  const [date, setDate] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});
  // 企微标签
  const [wechatLabel, setWechatLabel] = useState<string[]>([]);
  // 添加人
  const [addEmployee, setAddEmployee] = useState<number | null>(null);
  // 企微名称
  const [wechatName, setWechatName] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  // 防抖
  const debounceFn = useCallback(
    _.debounce((callback) => {
      callback();
    }, 1000),
    [],
  );

  // 初始化filter
  const initFilter = () => {
    setDate({});
    setWechatLabel([]);
    setAddEmployee(null);
    setWechatName(null);
    handleChangePage({}, 1);
    setId(null);
    setData([]);
  };

  useEffect(() => {
    setLoading(true);
    debounceFn(() => getData());
  }, [date, wechatLabel, addEmployee, wechatName, show, pageIndex]);

  const getFilter = () => {
    return {
      addTimeBegin: date.startDate,
      addTimeEnd: date.endDate,
      addEmployeeId: addEmployee,
      name: wechatName,
      tagIds: wechatLabel,
      pageIndex,
    };
  };

  const getData = () => {
    const params = getFilter();
    if (show) {
      webApi
        .searchRelation()
        .ajaxJson({
          data: params,
        })
        .then((res: { code: number, data: { items: any[], conf: PageConfig } }) => {
          if (res.code === 0) {
            const { items, conf } = res.data;
            if (_.isArray(items)) {
              const data = items.map((v) => {
                return {
                  ...v,
                  ...(v.onlineSalesCustomer || {}),
                };
              });
              setData(data || []);
              setPage(conf);
            }
          }
          setLoading(false);
        })
        .catch((err: { message: string }) => {
          setLoading(false);
          setData([]);
          setLoading(false);
          message.error(err.message);
        });
    }
  };

  const handleOptionalChange = useCallback((e: object, value: any) => {
    setId(value);
  }, []);

  const handleDate = (e: object, startDate: string, endDate: string) => {
    setDate({ startDate, endDate });
    handleChangePage({}, 1);
  };

  const handleWechatLabel = (e: object, value: string[]) => {
    setWechatLabel(value);
    handleChangePage({}, 1);
  };

  const handleAddEmployee = (e: object, value: number) => {
    setAddEmployee(value);
    handleChangePage({}, 1);
  };

  const handleWechatName = (e: object, value: string) => {
    setWechatName(value || null);
    handleChangePage({}, 1);
  };

  const onSubmit = (e: object) => {
    if (props.onSubmit) {
      // 传入onSubmit，不走默认提交
      const _data = _.filter(data, (d) => d.relationId === id);
      const [value] = _data;
      props.onSubmit(value || {});
      setShow(false);
      return;
    }
    const params = {
      relationId: id,
      contact: [{
        contactId: relation?.contactId,
        leadId: relation?.leadId,
      }],
    };
    setSubmitLoading(true);
    webApi
      .onlineSalesBind()
      .ajaxJson({
        data: params,
      })
      .then((res: { code: number, message: string }) => {
        if (res.code === 0) {
          setShow(false);
          setSubmitLoading(false);
          initFilter();
          props.callback && props.callback();
        }
      })
      .catch((err: { message: string }) => {
        setSubmitLoading(false);
        message.error(err?.message);
      });
  };

  const onClick = () => {
    setId(relation?.relationId);
    setShow(true);
  };

  const onClose = () => {
    setShow(false);
    initFilter();
  };

  const handleChangePage = (e: object, value: number) => {
    setPageIndex(value - 1);
  };

  const handleDisabledValue = useMemo(() => {
    const { account } = props;
    return () => (
      data.filter((v) => (v.employeeId !== account.user_id || v.status === WECHAT_CUSTOMER_STATUS.INVALID)).map((d) => d.relationId)
    );
  }, [data]);

  const ValueAreaComponent = useCallback((props: any) => {
    const { onOpen, label, onClear, date } = props;
    return (
      <div>
        <div
          className='datechoose'
          onClick={onOpen}
        >
          <span>添加日期: {label}</span>
        </div>
        {
          (date.startDate || date.endDate) && <div className="date-icon" style={{ cursor: 'pointer' }}>
            <i onClick={onClear} className="svg svg-del svg-del-16"></i>
          </div>
        }
      </div>
    );
  }, []);

  /**
   * @description: 筛选项
   */
  const renderFilter = useMemo(() => {
    return () => (
      <div className="wechat-associate-filter bill-filter">
        <div className="hd">
          <div className="fts">
            <span className="fts-text-tt">
              下发日期 :
            </span>
            <DatePicker
              format='xxxx-xx-xx xx:xx:xx'
              type='range'
              timePicker
              date={date}
              onChange={handleDate}
              // ValueAreaComponent={ValueAreaComponent}
              startDate={date.startDate}
              endDate={date.endDate}
              clear={true}
              style={{ width: 325 }}
              optionalStartRelativeDays={-OPTIONAL_START_RELATIVE_DAYS}
              optionalEndRelativeDays={0}
              longestRange={OPTIONAL_START_RELATIVE_DAYS}
            />
          </div>
          <div className="fts">
            <span className="fts-text-tt">
              企微标签 :
            </span>
            <WeChatLabelSelect multiple value={wechatLabel} onChange={handleWechatLabel}  />
          </div>
          <div className="fts">
            <span className="fts-text-tt">
              添加人 :
            </span>
            <TeamUserSelectWithoutSeat valueAreaText="添加人" value={addEmployee || null} onChange={handleAddEmployee} />
          </div>
          <div className="fts">
            <span className="fts-text-tt">
              企微名称 :
            </span>
            <Input value={wechatName} onChange={handleWechatName} />
          </div>
        </div>
      </div>
    );
  }, [date, wechatLabel, addEmployee, wechatName]);

  const renderColumn = (type: string, name: string, value: any, data: any) => {
    if (type === 'head') {
      return value;
    }
    if (!value && typeof value !== 'number') return '-';
    switch (name) {
      case 'tagNames':
        return value.join(',');
      case 'leads':
        const leadsStr = value.map((v: { leadId: number }) => v.leadId).join(',');
        return <OverlayTrigger trigger="hover" overlay={<PopOver>{leadsStr}</PopOver>}>
          {leadsStr}
        </OverlayTrigger>;
      case 'addWay':
        return fuzzyGetMessage('CustomerEmployeeRelationAddWay', `${value}`);
      default:
        return value;
    }
  };

  const onFocus = () => {
    onClick();
  };

  const onClear = () => {
    if (props.onSubmit) {
      props.onSubmit({});
    }
  };

  return (
    <React.Fragment>
      {
        mode === 'input' ? <div className='wechat-associate-input'><Input value={props.inputValue} onFocus={onFocus} />
        {
          props.inputValue && <span className="graph-icon-del size-sm clear-btn" onClick={onClear}>
          <i className="graph-icon-add graph-icon-close graph-icon size-sm spaui-component" data-spaui="{}">
            <i className="add-hor"></i>
            <i className="add-ver"></i>
          </i>
          </span>
        }
        </div> : <a href="javascript:;" onClick={onClick}>
        {titleText}
      </a>
      }
      <Dialog
        {...props}
        className="wechat-associate-modal"
        modalStyle={{
          zIndex: 9999,
        }}
        useNewUI
        show={show}
        disabled={!id || submitLoading}
        title={`${titleText}企微账号`}
        onSubmit={onSubmit}
        cancelLabel={`取消${titleText}`}
        submitLabel={`确认${titleText}`}
        onCancel={onClose}
        onClose={onClose}
      >
        {renderFilter()}
        <Table loading={loading} className="with-light-theme" data={data}>
          <Column
            type='optional'
            target='relationId'
            value={id}
            onOptionalChange={handleOptionalChange}
            disabledValue={handleDisabledValue()}
          ></Column>
          <Column name="name" render={renderColumn}>名称</Column>
          {/* <Column name="tagNames" render={renderColumn}>企微标签</Column> */}
          <Column name="corpName" render={renderColumn}>企微公司名称</Column>
          <Column name="userId">添加人</Column>
          <Column name="addTime">添加时间</Column>
          <Column name="addWay" render={renderColumn}>来源</Column>
          <Column name="description">描述</Column>
          <Column name="leads" render={renderColumn}>已关联线索</Column>
        </Table>
        <PageBar
          value={pageIndex + 1}
          pageSize={page.pageSize}
          totalSize={page.totalCount}
          onChange={handleChangePage}
          showPageJudge={false}
        />
      </Dialog>
    </React.Fragment>
  );
};

export default withAccount(withApi(WeChatAssociateModal));
